import React, { useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { ArrowRight, MapPin, Globe, TrendingUp, Users, Star, Building2 } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { updateMetaTags } from '@/utils/seoUtils';
import { generateHreflangLinks } from '@/lib/swissHreflang';

const EnglishSwissCityServicePage = () => {
  const { currentLanguage } = useLanguage();
  const location = useLocation();
  const pathParts = location.pathname.split('/');
  const service = pathParts[2]; // e.g., 'seo', 'web-design'
  const city = pathParts[3] || 'zurich'; // Default to Zurich

  // English-speaking market content for Switzerland
  const cityContent: { [key: string]: any } = {
    zurich: {
      name: 'Zurich',
      region: 'International Hub',
      description: {
        en: 'Switzerland\'s global financial center with the highest concentration of multinational corporations and expat professionals.',
        de: 'Schweizer globales Finanzzentrum mit der höchsten Konzentration multinationaler Unternehmen und Expat-Fachkräften.',
        fr: 'Centre financier mondial de la Suisse avec la plus forte concentration d\'entreprises multinationales et de professionnels expatriés.',
        it: 'Centro finanziario globale della Svizzera con la più alta concentrazione di aziende multinazionali e professionisti espatriati.'
      },
      industries: ['Finance', 'Technology', 'Pharma', 'Consulting'],
      population: '400,000',
      expats: '35%',
      localProof: {
        en: 'Over 200 international companies trust our digital expertise',
        de: 'Über 200 internationale Unternehmen vertrauen auf unsere digitale Expertise',
        fr: 'Plus de 200 entreprises internationales font confiance à notre expertise digitale',
        it: 'Oltre 200 aziende internazionali si fidano della nostra competenza digitale'
      },
      specialOffer: {
        en: 'Multilingual strategy tailored for international businesses in Switzerland',
        de: 'Mehrsprachige Strategie für internationale Unternehmen in der Schweiz',
        fr: 'Stratégie multilingue adaptée aux entreprises internationales en Suisse',
        it: 'Strategia multilingue su misura per aziende internazionali in Svizzera'
      }
    },
    geneva: {
      name: 'Geneva',
      region: 'International Organizations',
      description: {
        en: 'Home to UN headquarters and numerous NGOs, attracting global organizations and diplomatic missions.',
        de: 'Heimat des UN-Hauptquartiers und zahlreicher NGOs, zieht globale Organisationen und diplomatische Missionen an.',
        fr: 'Siège de l\'ONU et de nombreuses ONG, attirant des organisations mondiales et des missions diplomatiques.',
        it: 'Sede del quartier generale ONU e numerose ONG, attrae organizzazioni globali e missioni diplomatiche.'
      },
      industries: ['NGO', 'Diplomacy', 'Finance', 'Luxury'],
      population: '200,000',
      expats: '48%',
      localProof: {
        en: 'Serving 50+ international organizations and NGOs',
        de: '50+ internationale Organisationen und NGOs bedient',
        fr: 'Au service de 50+ organisations internationales et ONG',
        it: 'Al servizio di 50+ organizzazioni internazionali e ONG'
      },
      specialOffer: {
        en: 'Specialized digital solutions for NGOs and international organizations',
        de: 'Spezialisierte digitale Lösungen für NGOs und internationale Organisationen',
        fr: 'Solutions digitales spécialisées pour les ONG et organisations internationales',
        it: 'Soluzioni digitali specializzate per ONG e organizzazioni internazionali'
      }
    }
  };

  const serviceContent: { [key: string]: any } = {
    'seo': {
      name: { en: 'SEO', de: 'SEO', fr: 'Référencement', it: 'SEO' },
      description: {
        en: 'Strategic search engine optimization for international businesses operating in Switzerland.',
        de: 'Strategische Suchmaschinenoptimierung für internationale Unternehmen in der Schweiz.',
        fr: 'Optimisation stratégique des moteurs de recherche pour les entreprises internationales en Suisse.',
        it: 'Ottimizzazione strategica dei motori di ricerca per aziende internazionali in Svizzera.'
      },
      benefits: ['Multilingual SEO', 'Local + Global targeting', 'Compliance ready', 'Analytics & reporting']
    },
    'web-design': {
      name: { en: 'Web Design', de: 'Webdesign', fr: 'Conception Web', it: 'Web Design' },
      description: {
        en: 'Professional websites that resonate with international audiences while maintaining Swiss quality standards.',
        de: 'Professionelle Websites, die bei internationalen Zielgruppen ankommen und Schweizer Qualitätsstandards erfüllen.',
        fr: 'Sites web professionnels qui résonnent avec les audiences internationales tout en maintenant les standards de qualité suisses.',
        it: 'Siti web professionali che risuonano con il pubblico internazionale mantenendo gli standard di qualità svizzeri.'
      },
      benefits: ['International UX', 'Multi-currency', 'GDPR compliant', 'Mobile-first']
    },
    'ai-video': {
      name: { en: 'AI Video', de: 'AI-Video', fr: 'Vidéo IA', it: 'Video IA' },
      description: {
        en: 'Cutting-edge AI video production for international companies that need to communicate across cultures.',
        de: 'Modernste KI-Videoproduktion für internationale Unternehmen, die kulturübergreifend kommunizieren müssen.',
        fr: 'Production vidéo IA de pointe pour les entreprises internationales qui doivent communiquer à travers les cultures.',
        it: 'Produzione video IA all\'avanguardia per aziende internazionali che devono comunicare attraverso le culture.'
      },
      benefits: ['Multiple languages', 'Cultural adaptation', 'Fast delivery', 'Global distribution']
    },
    'google-business-profile': {
      name: { en: 'Google Business Profile', de: 'Google Business Profile', fr: 'Profil Google Business', it: 'Profilo Google Business' },
      description: {
        en: 'Professional management of your business presence to attract both local Swiss and international clients.',
        de: 'Professionelle Verwaltung Ihrer Unternehmenspräsenz zur Gewinnung lokaler Schweizer und internationaler Kunden.',
        fr: 'Gestion professionnelle de votre présence d\'entreprise pour attirer les clients suisses locaux et internationaux.',
        it: 'Gestione professionale della vostra presenza aziendale per attrarre clienti svizzeri locali e internazionali.'
      },
      benefits: ['Local visibility', 'International reach', 'Review management', 'Multi-language support']
    }
  };

  const currentService = serviceContent[service] || serviceContent['seo'];
  const currentCity = cityContent[city] || cityContent['zurich'];

  useEffect(() => {
    const pageTitle = `${currentService.name[currentLanguage]} ${currentCity.name} | International Business Solutions`;
    const pageDescription = currentService.description[currentLanguage] || currentService.description.en;
    const canonicalUrl = `https://futurelevel.ch${location.pathname}`;

    // Update meta tags
    updateMetaTags({
      title: pageTitle,
      description: pageDescription,
      canonicalUrl: canonicalUrl,
      language: currentLanguage
    });

    // Generate hreflang links - simplified for now
    // const hreflangLinks = generateHreflangLinks(location.pathname);

  }, [currentLanguage, service, city, location.pathname]);

  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <main className="flex-grow pt-20">
        {/* Hero Section */}
        <section className="py-16 bg-gradient-to-br from-background to-background/80 relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-primary/20 via-background to-background"></div>
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <Badge variant="outline" className="mb-4">
                <Building2 className="w-4 h-4 mr-2" />
                {currentCity.name} • International Business Hub
              </Badge>
              
              <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-foreground to-foreground/80 bg-clip-text text-transparent">
                {currentService.name[currentLanguage]} for International Companies in {currentCity.name}
              </h1>
              
              <p className="text-xl md:text-2xl text-muted-foreground mb-8 leading-relaxed">
                {currentService.description[currentLanguage]}
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
                <Button size="lg" className="bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary">
                  Free International Consultation
                  <ArrowRight className="ml-2" size={20} />
                </Button>
                <Button variant="outline" size="lg">
                  View Success Stories
                </Button>
              </div>

              {/* Quick Stats for International Market */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-2xl mx-auto">
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary">200+</div>
                  <div className="text-sm text-muted-foreground">International Clients</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary">{currentCity.expats}</div>
                  <div className="text-sm text-muted-foreground">Expat Population</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary">350%</div>
                  <div className="text-sm text-muted-foreground">Average ROI</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary">24/7</div>
                  <div className="text-sm text-muted-foreground">Global Support</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* International Market Focus */}
        <section className="py-16 bg-background/50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="grid md:grid-cols-2 gap-12 items-center">
                <div>
                  <h2 className="text-3xl font-bold mb-6">
                    Trusted by International Organizations
                  </h2>
                  <p className="text-lg text-muted-foreground mb-6">
                    {currentCity.localProof[currentLanguage]}
                  </p>
                  <p className="text-muted-foreground mb-6">
                    We understand the unique challenges of international businesses operating in Switzerland:
                    multicultural teams, compliance requirements, and the need to communicate across languages and cultures.
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    {currentCity.industries.map((industry: string, index: number) => (
                      <Badge key={index} variant="secondary" className="justify-center py-2">
                        {industry}
                      </Badge>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <Card className="p-6 text-center">
                    <TrendingUp className="w-8 h-8 text-green-500 mx-auto mb-2" />
                    <div className="text-2xl font-bold text-green-500">+350%</div>
                    <div className="text-sm text-muted-foreground">Average Growth</div>
                  </Card>
                  <Card className="p-6 text-center">
                    <Users className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                    <div className="text-2xl font-bold text-blue-500">200+</div>
                    <div className="text-sm text-muted-foreground">Global Clients</div>
                  </Card>
                  <Card className="p-6 text-center">
                    <Star className="w-8 h-8 text-yellow-500 mx-auto mb-2" />
                    <div className="text-2xl font-bold text-yellow-500">4.9/5</div>
                    <div className="text-sm text-muted-foreground">Client Rating</div>
                  </Card>
                  <Card className="p-6 text-center">
                    <Globe className="w-8 h-8 text-purple-500 mx-auto mb-2" />
                    <div className="text-2xl font-bold text-purple-500">15+</div>
                    <div className="text-sm text-muted-foreground">Languages</div>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* International Business Special */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl font-bold mb-8">
                International Business Package
              </h2>
              <Card className="p-8 bg-gradient-to-r from-primary/10 to-accent/10 border-primary/20">
                <div className="text-xl font-semibold mb-4">
                  {currentCity.specialOffer[currentLanguage]}
                </div>
                <ul className="text-left space-y-2 mb-6 max-w-2xl mx-auto">
                  {currentService.benefits.map((benefit: string, index: number) => (
                    <li key={index} className="flex items-center">
                      <ArrowRight className="w-4 h-4 text-primary mr-2 flex-shrink-0" />
                      {benefit}
                    </li>
                  ))}
                </ul>
                <div className="bg-white/20 p-4 rounded-lg mb-6">
                  <div className="text-sm text-muted-foreground mb-2">Special Features for International Clients:</div>
                  <div className="grid md:grid-cols-2 gap-2 text-sm">
                    <div>✓ Multi-timezone support</div>
                    <div>✓ Cultural adaptation</div>
                    <div>✓ Compliance assistance</div>
                    <div>✓ Expat-friendly service</div>
                  </div>
                </div>
                <Button size="lg" className="bg-gradient-to-r from-primary to-primary/90">
                  Request International Quote
                </Button>
              </Card>
            </div>
          </div>
        </section>

        {/* FAQ for International Clients */}
        <section className="py-16 bg-background/50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-12">
                FAQ for International Businesses
              </h2>
              
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-lg font-semibold mb-3">
                    Do you work with non-Swiss companies?
                  </h3>
                  <p className="text-muted-foreground">
                    Absolutely! We specialize in helping international companies establish and grow their presence in Switzerland. 
                    We understand visa requirements, cultural nuances, and local business practices.
                  </p>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold mb-3">
                    What languages do you support?
                  </h3>
                  <p className="text-muted-foreground">
                    We provide services in English, German, French, and Italian. Our team includes native speakers 
                    and cultural consultants who ensure your message resonates with local and international audiences.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-3">
                    How do you handle Swiss compliance requirements?
                  </h3>
                  <p className="text-muted-foreground">
                    We stay up-to-date with Swiss data protection laws (FADP), accessibility requirements, and industry regulations. 
                    All our solutions are built with compliance in mind from day one.
                  </p>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold mb-3">
                    Can you help with the Swiss market entry?
                  </h3>
                  <p className="text-muted-foreground">
                    Yes! We offer market entry consulting, competitive analysis, and localization strategies 
                    specifically designed for international companies entering the Swiss market.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-16">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold mb-6">
              Ready to Succeed in Switzerland?
            </h2>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Let's discuss how we can help your international business thrive in the Swiss market.
              Free consultation with our international business specialists.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" asChild className="bg-gradient-to-r from-primary to-primary/90">
                <a href={`/${currentLanguage}/contact/`}>
                  Schedule Free Consultation
                  <ArrowRight className="ml-2" size={20} />
                </a>
              </Button>
              <Button variant="outline" size="lg" asChild>
                <a href={`/${currentLanguage}/seo-roi-rechner/`}>
                  Calculate Your ROI
                </a>
              </Button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default EnglishSwissCityServicePage;